import Num from '@flipster/utils/num';

// import type이므로 런타임에 OrderbookRow(React 컴포넌트)를 로드하지 않는다 — 이 모듈은 dep-free 순수 로직
import type { OrderbookItem } from './OrderbookRow';

/** 오더북이 한쪽(ask/bid)에 표시하는 최대 레벨 수 */
export const orderbookMaxItems = 20;

const emptyItem: OrderbookItem = { price: null, size: null, sum: null, sumQty: null, avgPrice: null, qty: null };

/** aggregateOrderbook의 출력 형태: [버킷 가격, 수량 합, notional 합] */
export type AggregatedOrderbookLevel = [price: string, qty: string, notional: string];

/** 레벨별 누적 sum(notional)·sumQty·avgPrice를 계산해 표시용 OrderbookItem으로 변환한다 */
export function processOrderbookData(data: AggregatedOrderbookLevel[]): OrderbookItem[] {
  return data.reduce((acc: OrderbookItem[], [price, qty]) => {
    const prevSum = Num(acc.at(-1)?.sum ?? 0);
    const prevSumQty = Num(acc.at(-1)?.sumQty ?? 0);

    const currentSize = Num(price).multipliedBy(qty);
    const sum = Num(prevSum).plus(currentSize);
    const sumQty = Num(prevSumQty).plus(qty);

    const avgPrice = sum.dividedBy(sumQty);

    acc.push({ price: Num(price), size: currentSize, sum, sumQty, avgPrice, qty: Num(qty) });
    return acc;
  }, []);
}

/** maxLength에 맞춰 초과분은 자르고 부족분은 emptyItem으로 패딩한다 */
export function normalizeOrderbookItems(items: OrderbookItem[], maxLength: number): OrderbookItem[] {
  return [...items.slice(0, maxLength), ...Array(Math.max(0, maxLength - items.length)).fill(emptyItem)];
}

export function processAndNormalizeOrderbookData(data: AggregatedOrderbookLevel[], maxLength: number): OrderbookItem[] {
  return normalizeOrderbookItems(processOrderbookData(data), maxLength);
}

export function isSameOrderbookItem(a: OrderbookItem | undefined, b: OrderbookItem): boolean {
  if (!a) return false;
  if (a === b) return true;
  const eq = (x: OrderbookItem['price'], y: OrderbookItem['price']) => (!x || !y ? x === y : x.isEqualTo(y));
  // row/tooltip에 표시되는 값 전부를 비교한다. sumQty는 qty 모드 메인 row에 직접 노출되고,
  // avgPrice(=sum/sumQty)는 tooltip에 노출되므로 sum만으로는 부족 — sumQty까지 같아야 avgPrice도 같다.
  return eq(a.price, b.price) && eq(a.qty, b.qty) && eq(a.sum, b.sum) && eq(a.sumQty, b.sumQty);
}

/**
 * 값이 같은 슬롯은 이전 OrderbookItem 객체를 재사용해 identity를 유지한다.
 * 매 tick 모든 레벨이 새 객체로 만들어지면 OrderbookRow의 memo가 항상 깨지므로,
 * "값이 실제로 변한 row만" 재렌더되도록 하는 핵심 장치.
 * 모든 슬롯이 같으면 배열 자체도 이전 identity를 반환해 구독자 전파를 차단한다.
 */
export function mergeStableOrderbookItems(prev: OrderbookItem[], next: OrderbookItem[]): OrderbookItem[] {
  const merged = next.map((item, i) => (isSameOrderbookItem(prev[i], item) ? prev[i] : item));
  const isAllSame = prev.length === merged.length && merged.every((item, i) => item === prev[i]);
  return isAllSame ? prev : merged;
}

export interface BidAskRatio {
  bidPercent: number;
  askPercent: number;
  isEmpty: boolean;
}

/** 표시되는 상위 orderbookMaxItems 레벨의 수량 합으로 bid/ask 비율을 계산한다 */
export function computeBidAskRatio(
  aggregatedAsks: AggregatedOrderbookLevel[],
  aggregatedBids: AggregatedOrderbookLevel[],
): BidAskRatio {
  const displayedBids = aggregatedBids.slice(0, orderbookMaxItems);
  const displayedAsks = aggregatedAsks.slice(0, orderbookMaxItems);

  const totalBidQty = displayedBids.reduce((acc, [, qty]) => acc.plus(qty), Num(0));
  const totalAskQty = displayedAsks.reduce((acc, [, qty]) => acc.plus(qty), Num(0));
  const total = totalBidQty.plus(totalAskQty);

  if (total.isZero()) return { bidPercent: 50, askPercent: 50, isEmpty: true };

  const bidPercent = Math.round(totalBidQty.dividedBy(total).times(100).toNumber());
  return { bidPercent, askPercent: 100 - bidPercent, isEmpty: false };
}
